import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchWithAuth } from '../auth/fetchWithAuth';
import { API_URL } from '../../config/env';

const DEBOUNCE_MS = 300;

/**
 * Typeahead search against the organization's upstream ticketing system.
 *
 * `available` is false when the organization has no ticketing integration (the endpoint 404s),
 * which is the signal for the caller to fall back to a free-text ticket reference field. An
 * unreachable ticketing system (502) sets `error` but keeps `available` true: the integration
 * exists, so the caller still falls back to free text, but it must say why rather than quietly
 * presenting a field that no longer links anything.
 *
 * `error` is shown to the admin as written, so it has to read as a sentence. The API's own
 * message is used when there is one; the backend keeps exception text out of it.
 */
export default function useTicketSearch() {
  const [query, setQuery] = useState('');
  const [tickets, setTickets] = useState([]);
  // Projects the backend could not reach. A ticket search that returns nothing because a
  // configured project was deleted or lost permission is indistinguishable from one that simply
  // matched nothing, so the backend names them and the picker says so.
  const [unreachableProjects, setUnreachableProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [available, setAvailable] = useState(true);
  const requestId = useRef(0);

  const runSearch = useCallback(async (term, id) => {
    try {
      const res = await fetchWithAuth(
        `${API_URL}/v1/admin/ticketing/tickets?q=${encodeURIComponent(term)}`,
        { cache: 'no-store' }
      );

      // A later keystroke already superseded this request.
      if (id !== requestId.current) return;

      if (res.status === 404) {
        setAvailable(false);
        setTickets([]);
        return;
      }

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError(body.error || 'The ticket search failed.');
        setTickets([]);
        return;
      }

      const body = await res.json();
      setAvailable(true);
      setError(null);
      setTickets(body.tickets || []);
      setUnreachableProjects(body.unreachable_projects || []);
    } catch {
      if (id === requestId.current) {
        setError('The ticket search could not be completed.');
        setTickets([]);
      }
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!available) return undefined;

    const id = requestId.current + 1;
    requestId.current = id;
    setLoading(true);
    setError(null);

    const timer = setTimeout(() => runSearch(query, id), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, available, runSearch]);

  return { query, setQuery, tickets, loading, error, available, unreachableProjects };
}
